/**
 * LA FRASE MOTIVACIONAL DE CADA ENTRADA (2026-10-26).
 *
 * Pedido de la usuaria: «que cada vez que un usuario inicie sesión en el portal
 * master le aparezca una frase motivacional la cual pueda quitar con una equis y
 * continuar hacia el portal, por ejemplo a los usuarios master y los
 * coordinadores que les aparezca frases motivacionales de liderazgo».
 *
 * Aquí vive el GUION: los cuatro grupos, qué roles caen en cada uno y las frases.
 * La ventana que las muestra es `componentes/FraseMotivacional.tsx`, y la marca
 * de «acaba de iniciar sesión» la pone `entrar()` en `lib/sesion.tsx`.
 *
 * ── TRES DECISIONES QUE NO SE DEDUCEN DE LAS FRASES ─────────────────────────
 *
 *  1. SON PROPIAS Y SIN AUTOR. Las citas célebres que circulan en español llevan
 *     muchas veces un autor equivocado, y una cita mal atribuida en el portal de
 *     la empresa es un error que se lee todos los días. Ninguna es la traducción
 *     de una cita conocida.
 *
 *  2. NO LLEVAN CIFRAS NI GÉNERO. Ninguna promete un número (la regla del
 *     proyecto es no escribir cifras a mano) y ninguna concuerda con el género de
 *     quien la lee: «Buenos días» y no «Bienvenido», porque muchas cuentas son
 *     compartidas o de un área y no de una persona.
 *
 *  3. EL GRUPO SALE DEL ROL, NUNCA DEL NOMBRE DE USUARIO. Un rol nuevo que nadie
 *     clasifique cae en `equipo`, que vale para cualquiera; nunca se queda sin
 *     frase. El reparto de los 27 roles que existían el 2026-10-26 está escrito
 *     en `frases-motivacionales.test.ts`.
 */

export type GrupoFrase = 'liderazgo' | 'ventas' | 'creatividad' | 'equipo';

/**
 * Quién dirige personas. MASTER y los coordinadores regionales los nombró la
 * usuaria; las gerencias y las dos coordinaciones de Compras y Costo también
 * tienen gente a cargo, así que leen lo mismo.
 */
const ROLES_LIDERAZGO = [
  'MASTER', 'REGIONAL', 'GERENCIA', 'GERENCIACEI', 'GERENCIAFINANZAS',
  'GERENCIAVENTAS', 'COORDCOMPRASCOSTO',
];

/** La calle: los asesores, con el analista de cuentas clave dentro del rol. */
const ROLES_VENTAS = ['ASESOR'];

/** Quien crea lo que se ve de las marcas: trade, diseño, audiovisual, medios e I+D. */
const ROLES_CREATIVIDAD = [
  'TRADEMARKETING', 'ANALISTATRADEMKT', 'DISENO', 'AUDIOVISUAL',
  'MEDIOSYCOMUNICACION', 'IYD',
];

/**
 * El grupo de una sesión. Si alguien tuviera dos roles, gana el primero de este
 * orden: liderazgo, ventas, creatividad. El resto —cobranza, facturación,
 * almacén, finanzas, C.E.I., contraloría, compras, calidad— y cualquier rol que
 * se cree mañana van a `equipo`.
 */
export function grupoDeRoles(roles: readonly string[]): GrupoFrase {
  if (roles.some((r) => ROLES_LIDERAZGO.includes(r))) return 'liderazgo';
  if (roles.some((r) => ROLES_VENTAS.includes(r))) return 'ventas';
  if (roles.some((r) => ROLES_CREATIVIDAD.includes(r))) return 'creatividad';
  return 'equipo';
}

/*
 * El grupo NO se nombra en pantalla. La primera versión pintaba «Frase de
 * liderazgo» encima del saludo y la usuaria lo pidió fuera el mismo día: «solo
 * son los parámetros que te di para las frases, pero no hay que especificarlos
 * ahí». El grupo decide qué frases le tocan a cada quien y nada más.
 */
export const FRASES: Record<GrupoFrase, readonly string[]> = {
  liderazgo: [
    'Liderar no es llegar primero: es lograr que todo el equipo llegue.',
    'Un buen líder no tiene todas las respuestas; hace las preguntas que ponen al equipo en movimiento.',
    'El ejemplo que das hoy es la instrucción que tu equipo seguirá mañana.',
    'Las metas se alcanzan con números; los equipos se construyen con confianza.',
    'Escuchar a tu equipo es la forma más rápida de ver lo que tú no estás viendo.',
    'Delegar no es soltar la responsabilidad: es multiplicar lo que el equipo puede lograr.',
    'Reconoce en público y corrige en privado: así crece un equipo.',
    'Tu energía marca el ritmo del equipo. Que hoy sea contagiosa.',
    'Un líder se mide por las personas que hace crecer, no por las tareas que acumula.',
    'La claridad es una forma de respeto: un equipo que sabe hacia dónde va avanza más rápido.',
    'Cuando el resultado no llega, el liderazgo pregunta qué se puede mejorar antes de buscar culpables.',
    'Inspira con propósito, acompaña con presencia y exige con coherencia.',
    'El mejor plan es el que tu equipo entiende y hace suyo.',
    'Celebrar los avances pequeños es lo que sostiene el camino hacia los grandes logros.',
    'Liderar es servir: abre el camino para que cada persona dé lo mejor de sí.',
    'La confianza se gana con hechos pequeños, repetidos todos los días.',
    'Un equipo motivado supera la meta; un equipo escuchado supera cualquier obstáculo.',
    'Los datos te dicen dónde estás; tu liderazgo decide hasta dónde se llega.',
    'Lidera hoy como te hubiera gustado que te lideraran cuando empezaste.',
    'En los días difíciles el equipo mira a quien lo guía. Mantén la calma y el rumbo.',
    'Formar a otro líder es la mejor huella que puedes dejar.',
    'Hablar claro, decidir a tiempo y cumplir lo prometido: así se lidera.',
    'Detrás de cada cifra del portal hay personas. Lidéralas con el mismo cuidado que los números.',
    'Un equipo no recuerda las órdenes que recibió, recuerda cómo se le acompañó.',
  ],
  ventas: [
    'Cada visita es una oportunidad de convertir a un cliente en un aliado.',
    'El cliente no compra solo un producto: compra la confianza que le das.',
    'La constancia vence a la suerte. Una visita más puede ser la venta del mes.',
    'Escucha primero y ofrece después: la mejor venta empieza con una buena pregunta.',
    'Hoy es un buen día para superar tu propia marca.',
    'Un «no» de hoy es información para el «sí» de mañana.',
    'Tu ruta de hoy construye el resultado de tu mes. Recórrela con propósito.',
    'Los clientes recuerdan cómo los hiciste sentir más que lo que les vendiste.',
    'La meta no se alcanza el último día: se construye visita a visita.',
    'Conoce a tu cliente mejor que nadie y nadie podrá quitártelo.',
    'Cumplir lo que prometes es el mejor argumento de venta.',
    'Cada cliente bien atendido es la mejor publicidad de nuestras marcas.',
    'Los resultados de mañana se siembran con el trabajo de hoy.',
    'La actitud con la que tocas la puerta abre más puertas que el catálogo.',
    'Un cliente satisfecho vuelve; un cliente bien atendido te recomienda.',
    'No vendas por vender: resuelve una necesidad y la venta llega sola.',
    'Planificar tu día es la ventaja que nadie ve, pero que todos notan en el resultado.',
    'Cada cobro a tiempo también es una venta bien hecha.',
    'Celebra cada pedido: cada uno te acerca a la meta.',
    'El seguimiento convierte una buena visita en un cliente fiel.',
    'Sal hoy con la convicción de que tus marcas mejoran el día de tus clientes.',
    'Tu palabra viaja contigo en cada visita: cuídala como tu mejor herramienta.',
  ],
  creatividad: [
    'Las grandes ideas empiezan como preguntas que nadie se había hecho.',
    'Cada pieza que creas es la primera impresión de nuestras marcas. Hazla inolvidable.',
    'Probar, medir y ajustar: así una idea se convierte en un resultado.',
    'Una marca se construye con miles de detalles bien cuidados.',
    'Atrévete a proponer: la idea que hoy parece rara puede ser la campaña de mañana.',
    'Lo que el cliente ve en el punto de venta empieza en tu imaginación.',
    'Innovar no es cambiarlo todo: es mejorar algo cada día.',
    'Detrás de cada producto que enamora hay alguien que se atrevió a pensar distinto.',
    'Una buena historia mueve más que mil descuentos.',
    'Las ideas crecen cuando se comparten. Hoy, comparte una.',
    'Cada intento que no funcionó te acerca a la versión que sí funciona.',
    'Escucha al cliente final: ahí están las mejores ideas.',
    'Diseñar es resolver problemas con belleza.',
    'Cada exhibición bien hecha es una invitación a comprar.',
    'La curiosidad es la materia prima de la innovación. Cultívala.',
    'Una idea clara, bien contada, mueve a todo un mercado.',
    'Las marcas que se recuerdan son las que se atreven a ser distintas.',
    'Hoy puedes crear algo que alguien va a querer compartir.',
    'La constancia convierte una buena idea en una marca fuerte.',
    'Lo simple bien hecho siempre deja huella.',
  ],
  equipo: [
    'El trabajo bien hecho no necesita aplausos: se nota en los resultados de todos.',
    'Cada tarea que haces con cuidado sostiene el trabajo de muchas personas.',
    'La excelencia no es un acto aislado: es un hábito de todos los días.',
    'Nadie llega lejos sin su equipo. Hoy, apóyate en él y apóyalo.',
    'Un dato bien cargado, una factura a tiempo, un pedido completo: así se construye la confianza.',
    'Los procesos bien hechos son la base de los grandes resultados.',
    'Mejorar un poco cada día es la forma más segura de llegar lejos.',
    'La precisión de hoy evita los reprocesos de mañana.',
    'Tu trabajo es una pieza clave del engranaje. Sin él, nada gira igual.',
    'Cuando un compañero necesita ayuda, ese es el momento de demostrar que somos equipo.',
    'La calidad no es casualidad: es atención a los detalles.',
    'Hacer las cosas bien desde la primera vez es la forma más rápida de avanzar.',
    'Pregunta, aprende y comparte: así crece un equipo.',
    'Los pequeños logros de cada día suman las grandes metas del año.',
    'El orden de hoy es la tranquilidad de mañana.',
    'Un buen día de trabajo empieza con una actitud positiva. Contágiala.',
    'Cada cliente satisfecho tiene detrás el trabajo silencioso de muchas áreas. La tuya es una de ellas.',
    'La responsabilidad es cumplir aunque nadie esté mirando.',
    'Resolver un problema vale más que explicarlo dos veces.',
    'Juntos somos más fuertes, más rápidos y más precisos.',
    'Hoy es una nueva oportunidad para hacer las cosas un poco mejor que ayer.',
    'El compromiso convierte una tarea en un aporte.',
  ],
};

/**
 * Qué frase toca, SIN REPETIR hasta haberlas visto todas. Con una sola entrada
 * al día, la frase de hoy no vuelve a salir en un mes.
 *
 * `vistas` son los índices ya enseñados a esta persona en este grupo (los guarda
 * la ventana, en el navegador). Los que ya no existen —porque la lista se acortó—
 * se ignoran, y cuando no queda ninguna sin ver el ciclo vuelve a empezar, sin
 * repetir la última que salió. `azar` se inyecta para poder probarlo.
 */
export function elegirFrase(
  grupo: GrupoFrase,
  vistas: readonly number[],
  azar: () => number = Math.random,
): { indice: number; vistas: number[] } {
  const total = FRASES[grupo].length;
  const previas = vistas.filter((i) => Number.isInteger(i) && i >= 0 && i < total);
  let quedan = [...Array(total).keys()].filter((i) => !previas.includes(i));
  let base = previas;
  if (quedan.length === 0) {
    const ultima = previas[previas.length - 1];
    quedan = [...Array(total).keys()].filter((i) => i !== ultima || total === 1);
    base = [];
  }
  const indice = quedan[Math.min(quedan.length - 1, Math.floor(azar() * quedan.length))]!;
  return { indice, vistas: [...base, indice] };
}

/**
 * El nombre para el saludo. Varias cuentas se llaman «Persona · Cargo» y ahí se
 * saluda a la persona; el resto —«Cobranzas», «Asesor Vend. 102», «Centro
 * Llanos»— va tal cual. No se corta ningún nombre: lo que se quita es el cargo.
 */
export function nombreSaludo(nombre: string): string {
  const persona = nombre.split(' · ')[0]?.trim();
  return persona || nombre.trim();
}

/**
 * «Buenos días» hasta las 11:59, «Buenas tardes» hasta las 18:59 y luego «Buenas
 * noches». La hora que recibe es la de Caracas (la calcula la ventana).
 */
export function saludoDeHora(hora: number): string {
  if (hora >= 5 && hora < 12) return 'Buenos días';
  if (hora >= 12 && hora < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

/** Desde esta hora (de Caracas) la jornada laboral ya va terminando. */
export const HORA_CIERRE_JORNADA = 17;

/**
 * La línea del pie de la ventana. Pedido de la usuaria (2026-10-26): «si es
 * después de las cinco de la tarde, que no diga que tengas una excelente
 * jornada, porque a esa hora ya está acabando la jornada laboral». Desde las
 * 17:00 y hasta la medianoche se despide del cierre; antes, de la jornada.
 */
export function despedidaDeHora(hora: number): string {
  return hora >= HORA_CIERRE_JORNADA
    ? 'Que tengas un excelente cierre de jornada.'
    : 'Que tengas una excelente jornada.';
}
